import React, {
	useRef,
	useState,
	type ChangeEvent,
	type KeyboardEvent,
	type RefObject,
} from 'react';

import { useIntl, defineMessage } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { FadeIn, StaggeredEntrance } from '@atlaskit/motion';
import { Box, Flex, Pressable, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { defaultFooter } from './DefaultWrappers.tsx';
import type { MultipleChoiceToolProps } from './types.tsx';

type NavigationDirection = 'forward' | 'backward';

const FORWARD = 'forward' as const;
const BACKWARD = 'backward' as const;

const continuePlanningButtonLabel = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.continue-planning-button',
	defaultMessage: 'Continue planning',
	description: 'Button label to confirm answers and continue planning',
});
const skipAndContinuePlanningButtonLabel = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.skip-and-continue-planning-button',
	defaultMessage: 'Skip and continue planning',
	description: 'Button label to skip the last question and continue planning',
});
const skipButtonLabel = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.skip-button',
	defaultMessage: 'Skip',
	description: 'Button label to skip this question and advance to the next',
});
const nextButtonLabel = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.next-button',
	defaultMessage: 'Next',
	description: 'Button label to advance to the next question with the selected answer',
});
const previousQuestion = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.previous-question',
	defaultMessage: 'Previous question',
	description: 'Accessible label for the previous question navigation button',
});
const nextQuestion = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.next-question',
	defaultMessage: 'Next question',
	description: 'Accessible label for the next question navigation button',
});
const otherOptionPlaceholder = defineMessage({
	id: 'ai-agent-sessions.multi-choice.other-placeholder',
	defaultMessage: 'Tell {agentName} what to do',
	description: 'Placeholder text for the other option input in the multi-choice question tool',
});

const questionCounter = defineMessage({
	id: 'issue-ai-agent-sessions.multiple-choice.question-counter',
	defaultMessage: '{current} of {total}',
	description: 'Displays the current question number out of the total, e.g. "2 of 5"',
});

type MultipleChoiceOption = {
	description?: string;
	id: string;
	label: string;
};

type MultipleChoiceQuestion = {
	description?: string;
	id: string;
	options: MultipleChoiceOption[];
	question: string;
};

type OtherOptionAnswer = {
	customText: string;
	type: 'OTHER_OPTION';
};
type ProvidedOptionAnswer = {
	selectedOptionId: string;
	type: 'PROVIDED_OPTION';
};

type SkipAnswer = {
	type: 'SKIP';
};

type QuestionAnswer = OtherOptionAnswer | ProvidedOptionAnswer | SkipAnswer;

type ActionData = {
	questions?: MultipleChoiceQuestion[];
};

const isActionData = (data: unknown): data is ActionData => {
	if (typeof data !== 'object' || data === null) return false;
	const questions = Reflect.get(data, 'questions');
	return questions === undefined || Array.isArray(questions);
};

const isAnswerValid = (answer: QuestionAnswer | undefined): boolean =>
	!!answer &&
	answer.type !== 'SKIP' &&
	(answer.type === 'PROVIDED_OPTION' || answer.customText?.trim() !== '');

const resolveAnswer = (answer: QuestionAnswer | undefined): string | null => {
	if (!answer || answer.type === 'SKIP') return null;
	if (answer.type === 'OTHER_OPTION') return answer.customText ?? null;
	return answer.selectedOptionId;
};

const styles = cssMap({
	navHeader: {
		paddingBottom: token('space.150'),
	},
	navHeading: {
		flex: '1',
		flexShrink: '0',
	},
	optionList: {
		display: 'flex',
		flexDirection: 'column',
		listStyle: 'none',
		padding: '0',
		margin: '0',
	},
	optionCard: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		gap: token('space.200'),
		paddingInline: token('space.100'),
		marginBlock: token('space.050'),
		borderRadius: token('radius.medium'),
		textAlign: 'start',
		backgroundColor: token('color.background.neutral.subtle'),
	},
	optionCardWithHover: {
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	optionCardSelected: {
		backgroundColor: token('color.background.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
	},
	optionCardBody: {
		display: 'flex',
		flexDirection: 'column',
		flex: '1',
		minWidth: '0px',
	},
	numberBadge: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '20px',
		height: '20px',
		flexShrink: '0',
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border.bold'),
	},
	numberBadgeSelected: {
		borderColor: token('color.border.selected'),
		color: token('color.text.selected'),
	},
	otherTextfield: {
		flex: '1',
		marginLeft: token('space.negative.075'),
	},
});

type QuestionNavProps = {
	currentIndex: number;
	onNext: () => void;
	onPrevious: () => void;
	totalQuestions: number;
};

function QuestionNav({ currentIndex, totalQuestions, onPrevious, onNext }: QuestionNavProps) {
	const { formatMessage } = useIntl();

	return (
		<Flex alignItems="center" gap="space.050">
			<IconButton
				appearance="subtle"
				icon={ChevronLeftIcon}
				label={formatMessage(previousQuestion)}
				isDisabled={currentIndex === 0}
				onClick={onPrevious}
				spacing="compact"
			/>
			<Text color="color.text.subtlest">
				{formatMessage(questionCounter, { current: currentIndex + 1, total: totalQuestions })}
			</Text>
			<IconButton
				appearance="subtle"
				icon={ChevronRightIcon}
				label={formatMessage(nextQuestion)}
				isDisabled={currentIndex === totalQuestions - 1}
				onClick={onNext}
				spacing="compact"
			/>
		</Flex>
	);
}

type ActionOptionProps = {
	currentAnswer?: QuestionAnswer;
	index: number;
	onSelect: (answer: Record<string, ProvidedOptionAnswer>) => void;
	option: MultipleChoiceOption;
	questionId: string;
};

function ActionOption({ option, index, questionId, currentAnswer, onSelect }: ActionOptionProps) {
	const isSelected =
		currentAnswer?.type === 'PROVIDED_OPTION' && currentAnswer.selectedOptionId === option.id;

	return (
		<Pressable
			xcss={cx(
				styles.optionCard,
				styles.optionCardWithHover,
				isSelected && styles.optionCardSelected,
			)}
			onClick={() =>
				onSelect({ [questionId]: { type: 'PROVIDED_OPTION', selectedOptionId: option.id } })
			}
		>
			<Box xcss={cx(styles.numberBadge, isSelected && styles.numberBadgeSelected)}>
				{String(index)}
			</Box>
			<Box xcss={styles.optionCardBody}>
				<Text weight="bold" color={isSelected ? 'color.text.selected' : 'color.text'}>
					{option.label}
				</Text>
				{option.description && (
					<Text color={isSelected ? 'color.text.selected' : 'color.text.subtle'}>
						{option.description}
					</Text>
				)}
			</Box>
		</Pressable>
	);
}

type OtherOptionProps = {
	agentName: string;
	currentAnswer?: QuestionAnswer;
	index: number;
	inputRef?: RefObject<HTMLInputElement>;
	onSelect: (answer: OtherOptionAnswer) => void;
	onSubmit: (answer: Record<string, OtherOptionAnswer>) => void;
	questionId: string;
};

function OtherOption({
	index,
	currentAnswer,
	inputRef,
	agentName,
	questionId,
	onSelect,
	onSubmit,
}: OtherOptionProps) {
	const { formatMessage } = useIntl();
	const [text, setText] = useState(
		currentAnswer?.type === 'OTHER_OPTION' ? currentAnswer.customText : '',
	);

	const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
		const newText = e.currentTarget.value;
		setText(newText);
		onSelect({ type: 'OTHER_OPTION', customText: newText });
	};

	const handleOnSelect = () => {
		onSelect({ type: 'OTHER_OPTION', customText: text });
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && text.trim()) {
			e.preventDefault();
			onSubmit({ [questionId]: { type: 'OTHER_OPTION', customText: text.trim() } });
		}
	};

	return (
		<Box xcss={styles.optionCard} testId="ai-agent-sessions.option-other">
			<Box
				xcss={cx(
					styles.numberBadge,
					currentAnswer?.type === 'OTHER_OPTION' && styles.numberBadgeSelected,
				)}
			>
				{String(index)}
			</Box>
			<Box xcss={styles.otherTextfield}>
				<Textfield
					ref={inputRef}
					value={text}
					onChange={handleTextChange}
					onKeyDown={handleKeyDown}
					onFocus={handleOnSelect}
					placeholder={formatMessage(otherOptionPlaceholder, { agentName })}
					appearance="none"
				/>
			</Box>
		</Box>
	);
}

type ActionButtonsProps = {
	isCurrentAnswerValid: boolean;
	isLastQuestion: boolean;
	onSkip: () => void;
	onSubmit: () => void;
};

const ActionButtons = ({
	isLastQuestion,
	isCurrentAnswerValid,
	onSubmit,
	onSkip,
}: ActionButtonsProps) => {
	const { formatMessage } = useIntl();

	if (isLastQuestion) {
		return (
			<Button onClick={onSubmit}>
				{isCurrentAnswerValid
					? formatMessage(continuePlanningButtonLabel)
					: formatMessage(skipAndContinuePlanningButtonLabel)}
			</Button>
		);
	}

	if (isCurrentAnswerValid) {
		return <Button onClick={onSubmit}>{formatMessage(nextButtonLabel)}</Button>;
	}

	return (
		<Button onClick={onSkip} appearance="subtle">
			{formatMessage(skipButtonLabel)}
		</Button>
	);
};

export function MultipleChoiceQuestionsTool({
	message,
	onConfirm,
	agentSessionInfo,
	ContentWrapperComponent = React.Fragment,
	footer = defaultFooter,
}: MultipleChoiceToolProps): JSX.Element | null {
	const { agentName } = agentSessionInfo;
	const { sendAnalyticsEvent } = useAnalytics();
	const actionKey = message.actions?.[0]?.key ?? '';
	const [currentIndex, setCurrentIndex] = useState(0);
	const [direction, setDirection] = useState<NavigationDirection>(FORWARD);
	const [answers, setAnswers] = useState<Record<string, QuestionAnswer>>({});

	const action = message.actions?.[0];
	const { questions = [] } = (isActionData(action?.data) ? action?.data : undefined) ?? {};

	const currentQuestion = questions[currentIndex];
	const totalQuestions = questions.length;
	const isLastQuestion = currentIndex === totalQuestions - 1;

	const currentAnswer = currentQuestion ? answers[currentQuestion.id] : undefined;

	const isCurrentAnswerValid = isAnswerValid(currentAnswer);

	const handleOnPrevious = () => {
		setDirection(BACKWARD);
		setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'multipleChoiceQuestionsToolPreviousQuestion',
			eventType: 'ui',
			attributes: { actionKey },
		});
	};

	const advanceToNext = () => {
		setDirection(FORWARD);
		setCurrentIndex((prev) => (prev < totalQuestions - 1 ? prev + 1 : prev));
	};

	const handleOnNext = () => {
		// Implicitly skip if the current question has no answer selected
		if (currentQuestion && !answers[currentQuestion.id]) {
			setAnswers((prev) => ({ ...prev, [currentQuestion.id]: { type: 'SKIP' } }));
		}
		advanceToNext();
		sendAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'multipleChoiceQuestionsToolNextQuestion',
			eventType: 'ui',
			attributes: { actionKey },
		});
	};

	const handleOnSkip = () => {
		setAnswers((prev) => ({
			...prev,
			[currentQuestion.id]: { type: 'SKIP' },
		}));
		advanceToNext();
	};

	const handleOnSubmit = async (pendingAnswers?: Record<string, QuestionAnswer>) => {
		if (pendingAnswers) {
			setAnswers((prev) => ({ ...prev, ...pendingAnswers }));
		}

		if (!isLastQuestion) {
			advanceToNext();
			return;
		}

		if (!action) {
			return;
		}

		const mergedAnswers = pendingAnswers ? { ...answers, ...pendingAnswers } : answers;

		const totalQuestionsCount = questions.length;
		const questionsSkipped = questions.filter(
			(q) => !mergedAnswers[q.id] || mergedAnswers[q.id].type === 'SKIP',
		).length;
		const questionsWithOtherSelected = Object.values(mergedAnswers).filter(
			(a) => a != null && a.type === 'OTHER_OPTION',
		).length;

		await onConfirm(
			undefined,
			undefined,
			{
				actions: [
					{
						...action,
						// https://jplat.atlassian.net/browse/GRAVITYAI-4694 Currently, convoAI doesn't include the toolSource via the agent assignment flow. This is a temporary fix
						toolSource: action.toolSource ?? 'CONVO_AI',
						data: {
							questions: questions.map((q) => ({
								...q,
								answer: resolveAnswer(mergedAnswers[q.id]),
							})),
						},
					},
				],
			},
			{
				totalQuestionsCount,
				questionsSkipped,
				questionsWithOtherSelected,
				isPreExecution: true,
			},
			{ isAgentCollaboration: true },
		);
	};

	const handleOptionSelect = (answer: QuestionAnswer) => {
		if (!currentQuestion) return;
		setAnswers((prev) => ({
			...prev,
			[currentQuestion.id]: answer,
		}));
	};

	const otherOptionRef = useRef<HTMLInputElement>(null);

	const handleNumberKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
		// Don't intercept if the user is typing in a text input (e.g. the Other option textfield)
		if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

		const num = parseInt(e.key, 10);
		if (isNaN(num) || num < 1 || !currentQuestion) return;

		const optionIndex = num - 1;

		if (optionIndex < currentQuestion.options.length) {
			// Select and auto-submit the corresponding provided option
			const option = currentQuestion.options[optionIndex];
			void handleOnSubmit({
				[currentQuestion.id]: { type: 'PROVIDED_OPTION', selectedOptionId: option.id },
			});
		} else if (optionIndex === currentQuestion.options.length) {
			// Focus the Other option textfield without inserting the key character
			e.preventDefault();
			otherOptionRef.current?.focus();
		}
	};

	if (!currentQuestion) {
		return null;
	}

	const enterFrom = direction === BACKWARD ? 'left' : 'right';

	const bodyContent = (
		<Box onKeyDown={handleNumberKeyDown}>
			<Flex
				xcss={styles.navHeader}
				alignItems="start"
				justifyContent="space-between"
				gap="space.100"
			>
				<Box xcss={styles.navHeading}>
					<FadeIn key={`header-${currentIndex}`} entranceDirection={enterFrom}>
						{(motionProps) => (
							<div {...motionProps}>
								<Flex direction="column" gap="space.050">
									<Text weight="bold">{currentQuestion.question}</Text>
									{currentQuestion.description && (
										<Text color="color.text.subtle">{currentQuestion.description}</Text>
									)}
								</Flex>
							</div>
						)}
					</FadeIn>
				</Box>
				{totalQuestions > 1 && (
					<QuestionNav
						currentIndex={currentIndex}
						totalQuestions={totalQuestions}
						onPrevious={handleOnPrevious}
						onNext={handleOnNext}
					/>
				)}
			</Flex>
			<Box as="ul" xcss={styles.optionList}>
				<StaggeredEntrance key={currentQuestion.id} delayStep={50}>
					{[
						...currentQuestion.options.map((option, i) => (
							<FadeIn key={`${currentQuestion.id}-option-${i}`} entranceDirection={enterFrom}>
								{(motionProps) => (
									<li {...motionProps}>
										<ActionOption
											option={option}
											index={i + 1}
											questionId={currentQuestion.id}
											currentAnswer={currentAnswer}
											onSelect={handleOnSubmit}
										/>
									</li>
								)}
							</FadeIn>
						)),
						<FadeIn key={`other-${currentQuestion.id}`} entranceDirection={enterFrom}>
							{(motionProps) => (
								<li {...motionProps}>
									<OtherOption
										index={currentQuestion.options.length + 1}
										currentAnswer={currentAnswer}
										inputRef={otherOptionRef}
										agentName={agentName}
										questionId={currentQuestion.id}
										onSelect={handleOptionSelect}
										onSubmit={handleOnSubmit}
									/>
								</li>
							)}
						</FadeIn>,
					]}
				</StaggeredEntrance>
			</Box>
		</Box>
	);

	const actionButtons = (
		<ActionButtons
			isLastQuestion={isLastQuestion}
			isCurrentAnswerValid={isCurrentAnswerValid}
			onSubmit={handleOnSubmit}
			onSkip={handleOnSkip}
		/>
	);

	return (
		<Box>
			<ContentWrapperComponent>{bodyContent}</ContentWrapperComponent>
			{footer(actionButtons)}
		</Box>
	);
}
